import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Layers, Monitor, Cpu, Shield } from "lucide-react";

const cards = [
  {
    icon: Layers,
    title: "Fintech and Payments",
    description: "Financial infrastructure, treasury, and regulated payment systems.",
  },
  {
    icon: Monitor,
    title: "B2B Software",
    description: "Vertical SaaS and mission-critical enterprise platforms.",
  },
  {
    icon: Cpu,
    title: "Applied Operational Systems",
    description: "AI-enabled systems deployed in real operating environments.",
  },
  {
    icon: Shield,
    title: "Infrastructure",
    description: "Security, compliance, and developer tooling.",
  },
];

export default function InvestmentFocus() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="investment-focus"
      data-testid="investment-focus-section"
      className="relative py-[30px] md:py-[50px]"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-[34px] md:mb-[54px]"
        >
          <h2
            data-testid="investment-focus-heading"
            className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a] text-center"
          >
            Investment focus
          </h2>
        </motion.div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((card, i) => (
            <motion.div
              key={card.title}
              data-testid={`investment-card-${i + 1}`}
              initial={{ opacity: 0, y: 60, scale: 0.95 }}
              animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6, boxShadow: "0 20px 40px rgba(15,23,42,0.12), 0 4px 12px rgba(15,23,42,0.06)", transition: { duration: 0.15 } }}
              className="card-gradient-line group rounded-2xl border border-black/[0.04] bg-white px-[27px] py-8 cursor-default"
              style={{ boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
            >
              {/* Icon */}
              <div className="w-12 h-12 rounded-xl bg-blue-50 border border-blue-100/60 flex items-center justify-center mb-6">
                <card.icon className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
              </div>

              {/* Title */}
              <h3 className="font-body text-[17px] font-semibold text-[#08122a] mb-3 tracking-tight">
                {card.title}
              </h3>

              {/* Description */}
              <p className="font-body text-sm leading-relaxed text-[#475569]">
                {card.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
